import { useEffect, useState } from "react";
import { Modal, Button } from "react-bootstrap";
import { FaCheck } from "react-icons/fa";
import { ImTv } from "react-icons/im";
import { fetchWithAuth } from "../api/fetchWithAuth";
import EraTestCaseModal from "./EraTestCaseModal";

function EraRequirementModal({ show, onClose, requirement, apiBase = "/api/era-requirements", title = "ERA Requirement" }) {
  const [details, setDetails] = useState(null);
  const [loading, setLoading] = useState(false);
  const [selectedTestCase, setSelectedTestCase] = useState(null);
  const [showTestCaseModal, setShowTestCaseModal] = useState(false);

  const getRowStyle = (index) => ({
    "--bs-table-bg": index % 2 === 0 ? "rgba(25, 135, 84, 0.06)" : "#ffffff"
  });

  useEffect(() => {
    if (!show || !requirement?.id) {
      setDetails(null);
      setLoading(false);
      setSelectedTestCase(null);
      setShowTestCaseModal(false);
      return;
    }

    let cancelled = false;

    const loadDetails = async () => {
      setLoading(true);

      try {
        const res = await fetchWithAuth(`${apiBase}/${requirement.id}/details`);

        if (!res.ok) {
          if (!cancelled) {
            setDetails(null);
          }
          return;
        }

        const data = await res.json();
        if (!cancelled) {
          setDetails(data);
        }
      } catch (err) {
        if (!cancelled) {
          setDetails(null);
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    };

    loadDetails();

    return () => {
      cancelled = true;
    };
  }, [show, requirement, apiBase]);

  if (!requirement) return null;

  const coveringTestCases = details?.coveringTestCases ?? [];
  const openTestCase = (testCase) => {
    setSelectedTestCase(testCase);
    setShowTestCaseModal(true);
  };

  return (
    <>
    <Modal show={show} onHide={onClose} size="lg">
      <Modal.Header closeButton>
        <Modal.Title>
          {title} {requirement.req_number}
        </Modal.Title>
      </Modal.Header>

      <Modal.Body>
        {loading ? (
          <div>Loading...</div>
        ) : (
          <>
            <table className="table">
              <tbody>
                <tr style={getRowStyle(0)}>
                  <th>Title</th>
                  <td>{details?.req_title ?? requirement.req_title}</td>
                </tr>
                <tr style={getRowStyle(1)}>
                  <th>DMI</th>
                  <td>
                    {(details?.dmi ?? requirement.dmi) && (
                      <FaCheck className="text-success" aria-label="Yes" />
                    )}
                  </td>
                </tr>
                <tr style={getRowStyle(2)}>
                  <th>DMI IN</th>
                  <td>
                    {(details?.dmi_in_all ?? requirement.dmi_in_all) && (
                      <FaCheck className="text-success" aria-label="Yes" />
                    )}
                  </td>
                </tr>
                <tr style={getRowStyle(3)}>
                  <th>DMI OUT</th>
                  <td>
                    {(details?.dmi_out_all ?? requirement.dmi_out_all) && (
                      <FaCheck className="text-success" aria-label="Yes" />
                    )}
                  </td>
                </tr>
              </tbody>
            </table>

            <div className="mt-4">
              <h6 className="mb-3">Covering Test Cases ({coveringTestCases.length})</h6>

              <div className="table-responsive">
                <table className="table table-sm align-middle mb-0" style={{ fontSize: "0.86rem" }}>
                  <thead className="table-light">
                    <tr>
                      <th style={{ width: "90px" }}>Feature</th>
                      <th style={{ width: "90px" }}>Test case</th>
                      <th>Title</th>
                    </tr>
                  </thead>
                  <tbody>
                    {coveringTestCases.length === 0 ? (
                      <tr>
                        <td colSpan="3" className="text-center text-muted py-3">
                          No covering test cases found.
                        </td>
                      </tr>
                    ) : coveringTestCases.map((testCase, index) => (
                      <tr key={`${testCase.ft_number}-${testCase.tc_number}-${index}`} style={getRowStyle(index)}>
                        <td className="fw-semibold">{testCase.ft_number}</td>
                        <td className="fw-semibold">
                          <button
                            type="button"
                            className="btn btn-link p-0 fw-semibold text-decoration-none"
                            onClick={() => openTestCase(testCase)}
                          >
                            {testCase.tc_number}
                          </button>
                        </td>
                        <td>
                          <div className="d-flex align-items-start gap-2">
                            {testCase.hasDmi && (
                              <span
                                className="d-inline-flex align-items-center justify-content-center flex-shrink-0 text-primary"
                                style={{
                                  width: "22px",
                                  height: "22px",
                                  marginTop: "1px"
                                }}
                                aria-label="Contains DMI"
                                title="Contains DMI"
                              >
                                <ImTv size={13} />
                              </span>
                            )}
                            <span>{testCase.tc_title}</span>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </>
        )}
      </Modal.Body>

      <Modal.Footer>
        <Button variant="secondary" onClick={onClose}>
          Close
        </Button>
      </Modal.Footer>
    </Modal>

    <EraTestCaseModal
      show={showTestCaseModal}
      onClose={() => setShowTestCaseModal(false)}
      testCase={selectedTestCase}
    />
    </>
  );
}

export default EraRequirementModal;

